import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Container } from './ui';
import {
  blogPostBreadcrumbItems,
  pageBreadcrumbItems,
  type BreadcrumbItem,
} from '../lib/jsonld';
import { toEnPath, type Lang } from '../lib/routes';
import styles from './Breadcrumb.module.css';

const DARK_HERO_FR_PATHS = new Set([
  '/solutions',
  '/solutions/conformite',
  '/catalogue',
  '/buyback',
  '/reprise-parc-informatique',
  '/expertise',
  '/references',
  '/about',
  '/contact',
  '/status',
  '/mentions-legales',
  '/blog',
]);

function isDarkHeroPath(pathname: string): boolean {
  for (const fr of DARK_HERO_FR_PATHS) {
    if (pathname === fr || pathname === toEnPath(fr)) return true;
  }
  if (/^\/(en\/)?blog\/[^/]+$/.test(pathname)) return true;
  return false;
}

function useBreadcrumbItems(lang: Lang): BreadcrumbItem[] | null {
  const { pathname } = useLocation();
  const { slug } = useParams<{ slug?: string }>();
  const { t } = useTranslation('blog');

  return useMemo(() => {
    const isBlogPost = /^\/(en\/)?blog\/[^/]+$/.test(pathname);
    if (isBlogPost && slug) {
      const articles = t('articles', { returnObjects: true }) as Record<
        string,
        { title: string; slug: string } | undefined
      >;
      const post = articles[slug];
      if (!post) return null;
      return blogPostBreadcrumbItems(post.title, post.slug, lang);
    }
    return pageBreadcrumbItems(pathname, lang);
  }, [pathname, slug, lang, t]);
}

export function Breadcrumb() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'fr';
  const items = useBreadcrumbItems(lang);

  if (!items || items.length < 2) return null;
  if (isDarkHeroPath(pathname)) return null;

  const ariaLabel = lang === 'fr' ? "Fil d'Ariane" : 'Breadcrumb';

  return (
    <div className={styles.wrapper}>
      <Container>
        <nav className={styles.nav} aria-label={ariaLabel}>
          <ol className={styles.list}>
            {items.map((item, idx) => {
              const isLast = idx === items.length - 1;
              return (
                <li key={item.path} className={styles.item}>
                  {isLast ? (
                    <span className={styles.current} aria-current="page">
                      {item.name}
                    </span>
                  ) : (
                    <Link to={item.path} className={styles.link}>
                      {item.name}
                    </Link>
                  )}
                  {!isLast && (
                    <span className={styles.separator} aria-hidden="true">
                      ›
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </Container>
    </div>
  );
}
